<template>
  <div class="trail">
    <div class="trail__head">
      <app-brand-mark :size="28" />
    </div>

    <!-- Liste scrollt bei wenig Höhe, das Profil bleibt darunter stehen -->
    <div class="trail__scroll">
      <nav class="trail__nav" aria-label="Hauptnavigation">
        <ul class="trail__list">
          <li v-for="group in groups" :key="group.id" class="trail__entry">
            <router-link
              v-if="!group.children"
              :to="group.path"
              class="trail__item"
              :class="{ 'trail__item--here': isActivePath(group.path) }"
              :aria-current="isActivePath(group.path) ? 'page' : null"
            >
              <app-icon :name="group.icon" :size="22" class="trail__icon" />
              <span class="trail__label">{{ label(group) }}</span>
            </router-link>
            <app-nav-flyout v-else :group="group" :active-path="activePath" touch>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="trail__item"
                  :class="{ 'trail__item--here': group.id === activeGroupId }"
                  v-bind="attrs"
                  :aria-current="group.id === activeGroupId ? 'true' : null"
                  v-on="on"
                >
                  <app-icon :name="group.icon" :size="22" class="trail__icon" />
                  <span class="trail__label">{{ label(group) }}</span>
                </button>
              </template>
            </app-nav-flyout>
          </li>
        </ul>
      </nav>
    </div>

    <div class="trail__foot">
      <app-user-menu placement="right" :nudge="24" :width="264" @sign-out="$emit('sign-out')">
        <template v-slot:activator="{ on, attrs }">
          <button
            type="button"
            class="trail__user"
            v-bind="attrs"
            aria-haspopup="menu"
            :aria-label="'Benutzermenü von ' + (userTitle || 'Benutzer')"
            v-on="on"
          >
            <app-avatar :name="name" :size="40" ring />
          </button>
        </template>
      </app-user-menu>
    </div>
  </div>
</template>

<script>
// Touch-Rail 104 (Touch-Dichte ab 600 px; Spezifikation 2.11, gen_r3.py:873–921): Punkte 88 × 56 mit Icon 22 über
// Label 11/14, Gruppen öffnen ihr Flyout in Touch-Maßen. Kein Ausklappen, keine Tooltips. Oben der SIMA-Stern auf der
// Zeile des Seitenkopfs, darunter die Liste (scrollt bei wenig Höhe); unten der Avatar-Knopf 56, so weit innen wie
// der Stern oben.
import AppNavFlyout from './AppNavFlyout.vue';
import AppBrandMark from './AppBrandMark.vue';
import AppUserMenu from './AppUserMenu.vue';
import { normalizePath, userDisplayName, userLoginName } from '../../shell/navigation';

// Weicher Trennstrich (U+00AD) für lange Labels: bricht nur um, wenn das Label nicht in die Kachel passt
const SHY = String.fromCharCode(0xad);
const LABELS = { Tarifverwaltung: 'Tarif' + SHY + 'verwaltung' };

export default {
  name: 'AppTouchRail',
  components: { AppBrandMark, AppNavFlyout, AppUserMenu },
  props: {
    groups: { type: Array, required: true },
    activePath: { type: String, default: '' },
    activeGroupId: { type: String, default: null }
  },
  computed: {
    name() {
      return userDisplayName(this.$store.state.profile);
    },
    // wie der Benutzerknopf der Seitenleiste: Name und Benutzername
    userTitle() {
      return [this.name, userLoginName(this.$store.state.profile)].filter(Boolean).join(' · ');
    }
  },
  methods: {
    isActivePath(path) {
      return normalizePath(path) === this.activePath;
    },
    label(group) {
      return LABELS[group.title] || group.title;
    }
  }
};
</script>

<style scoped>
/* feste Breite: der Inhalt bleibt stehen, während Vuetify die Breite des Drawers animiert. Oben 21 wie Seitenleiste
   und Rail (Kopfzeile 44 auf der Zeile des Seitenkopfs in Touch-Dichte); unten 21: der Avatar (40 im Knopf 56) endet
   29 über der Fensterkante, so wie der Stern (28 in der Kopfzeile 44) oben 29 darunter beginnt. */
.trail {
  width: 104px;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 21px 8px 21px;
  font-family: var(--font);
}
.trail__head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  margin-bottom: 12px;
}
/* Liste in einem Scrollbereich mit Scrollschatten (nur wenn Inhalt verdeckt ist); Innenraum für
   Fokusringe */
.trail__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: -4px -8px;
  padding: 4px 8px;
  background:
    linear-gradient(var(--chrome) 30%, transparent) top / 100% 24px no-repeat local,
    linear-gradient(transparent, var(--chrome) 70%) bottom / 100% 24px no-repeat local,
    radial-gradient(farthest-side at 50% 0, var(--sh-scroll), transparent) top / 100% 8px no-repeat scroll,
    radial-gradient(farthest-side at 50% 100%, var(--sh-scroll), transparent) bottom / 100% 8px no-repeat scroll;
  background-color: var(--chrome);
}
.trail__nav {
  flex: none;
}
.trail__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.trail__entry {
  display: flex;
}
.trail__item {
  width: 88px;
  height: 56px;
  flex: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
  padding: 0 1px;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-2);
  font-family: var(--font);
  font-size: 11px;
  line-height: 14px;
  font-weight: 500;
  letter-spacing: -0.1px;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.trail__icon {
  flex: none;
  color: var(--ink-3);
}
.trail__label {
  max-width: 100%;
  white-space: normal;
  -webkit-hyphens: manual;
  hyphens: manual;
}
:where(html:not(.dichte-touch)) .trail__item:hover,
.trail__item[aria-expanded="true"] {
  background: var(--seg-hov);
}
/* aktiv: graue Fläche (--here) ohne Kante und Schatten (der Ort, kein Knopf), Icon orange, Label fett */
.trail__item.trail__item--here {
  background: var(--here);
  color: var(--ink);
  font-weight: 700;
}
.trail__item.trail__item--here .trail__icon {
  color: var(--acc);
}
.trail__item:active {
  background: var(--seg-press);
}

/* Benutzerblock ohne Trennlinie */
.trail__foot {
  flex: none;
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.trail__user {
  width: 56px;
  height: 56px;
  flex: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  font-family: var(--font);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .trail__user:hover,
.trail__user[aria-expanded="true"] {
  background: var(--seg-hov);
}
.trail__user:active {
  background: var(--seg-press);
}
</style>
